/**
 * NormalModeStrategy
 * 
 * Strategy for the normal map mode (without Digital Twin or I&I active).
 * This is the default mode of the dashboard.
 */

import React from 'react';
import { getInfreaestructureById, getReadingsV2 } from '@/services/measurement';
import {
	MapModeStrategy,
	Infrastructure,
	PopupData,
	FetchParams,
	MapModeContext,
	DigitalTwinContext,
} from './MapModeStrategy';

// Lazy import of the Popup component to avoid circular dependencies
const PopupComponent = React.lazy(() => import('../Popup.jsx'));

class NormalModeStrategy implements MapModeStrategy {
	name = 'normal';
	displayName = 'Normal Lens';
	dragRotate = true;
	touchRotate = true;

	/**
	 * In normal mode, filter only by selected infrastructure types
	 */
	filterInfrastructures(
		infras: Infrastructure[],
		context: MapModeContext,
		selectedTypes: string[]
	): Infrastructure[] {
		if (!infras) return [];
		
		return infras.filter((infra) => 
			selectedTypes.includes(infra.infrastructureTypeName)
		);
	}

	/**
	 * In normal mode, all icons have full opacity
	 */
	calculateOpacities(
		infras: Infrastructure[],
		context: MapModeContext,
		digitalTwinContext?: DigitalTwinContext
	): Record<number, number> {
		const opacities: Record<number, number> = {};
		
		if (!infras) return opacities;
		
		infras.forEach((infra) => {
			opacities[infra.measurementeInfraestructureId] = 1.0;
		});
		
		return opacities;
	}

	/**
	 * In normal mode, fetch /principal and /readings/v2 in parallel.
	 * The pre-fetched readings are attached to PopupData so Popup.jsx can skip its own fetch.
	 */
	async handleAssetClick(
		infra: Infrastructure,
		context: MapModeContext
	): Promise<PopupData> {
		const mt = infra.mainMeasurementTarget;
		try {
			const [response, readingsData] = await Promise.all([
				getInfreaestructureById(infra.measurementeInfraestructureId),
				mt?.measurementTargetId
					? getReadingsV2(mt.measurementTargetId)
					: Promise.resolve(null),
			]);

			if (response) {
				return {
					...response,
					expired: mt?.expired,
					prefetchedReadings: readingsData,
				};
			}

			// Fallback: return basic data if fetch fails
			return {
				measurementeInfraestructureId: infra.measurementeInfraestructureId,
				measurementInfraestructureName: infra.measurementInfraestructureName,
				infrastructureTypeName: infra.infrastructureTypeName,
				expired: mt?.expired,
			};
		} catch (error) {
			console.error('Error fetching infrastructure data:', error);

			// Return basic data in case of error
			return {
				measurementeInfraestructureId: infra.measurementeInfraestructureId,
				measurementInfraestructureName: infra.measurementInfraestructureName,
				infrastructureTypeName: infra.infrastructureTypeName,
				expired: mt?.expired,
			};
		}
	}

	/**
	 * Popup component for normal mode
	 */
	PopupComponent = PopupComponent;

	/**
	 * In normal mode, no additional parameters are sent to the fetch
	 */
	getFetchParams(context: MapModeContext): FetchParams {
		return {
			mode: '',
		};
	}

	/**
	 * In normal mode, there are no specific highlighted assets
	 */
	getHighlightedAssets(
		context: MapModeContext,
		digitalTwinContext?: DigitalTwinContext
	): Set<number> | null {
		return null;
	}

	/**
	 * Props for the popup in normal mode
	 */
	getPopupProps(
		infraInfo: PopupData,
		context: MapModeContext,
		additionalData?: any
	): Record<string, any> {
		return {
			data: infraInfo,
			id: infraInfo.measurementeInfraestructureId,
		};
	}
}

// Export singleton instance
export const normalModeStrategy = new NormalModeStrategy();
export default NormalModeStrategy;
